"use client"

import React from "react"
import AppIcon from "@/app/components/atomic/AppIcon"
import { TravelMotif } from "@/app/stores/travel/travel-comparator.store"
import { TRAVEL_MOTIF_OPTIONS } from "../travelFormConfig"

interface Props {
    value: TravelMotif
    onChange: (motif: TravelMotif) => void
}

const MotifIcon = ({ icon, selected }: { icon: string; selected: boolean }) => (
    <span
        className={`flex size-8 shrink-0 items-center justify-center rounded-[10px] ${selected ? "bg-white" : "bg-neutral"}`}
        aria-hidden="true"
    >
        <AppIcon iconClassName={icon} size="md" className="text-deep-green" />
    </span>
)

// Tiles on desktop, native dropdown on mobile to save height
export default function TravelMotifField({ value, onChange }: Props) {
    const selected = TRAVEL_MOTIF_OPTIONS.find((option) => option.value === value) ?? TRAVEL_MOTIF_OPTIONS[0]

    return (
        <div className="flex flex-col gap-2.5">
            <div role="radiogroup" aria-label="Type de séjour" className="hidden gap-2.5 sm:grid sm:grid-cols-3">
                {TRAVEL_MOTIF_OPTIONS.map((option) => {
                    const isSelected = option.value === value
                    return (
                        <button
                            key={option.value}
                            type="button"
                            role="radio"
                            aria-checked={isSelected}
                            data-test={`travel-motif-${option.value}`}
                            onClick={() => onChange(option.value)}
                            className={`flex min-h-[52px] cursor-pointer items-center gap-2.5 rounded-[14px] border-2 px-3 py-2 text-left text-sm text-pale transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-blue ${
                                isSelected ? "border-rm-blue bg-rm-blue/10 font-bold" : "border-[#EFE6E2] bg-white hover:border-rm-gray"
                            }`}
                        >
                            <MotifIcon icon={option.icon} selected={isSelected} />
                            <span className="leading-tight">{option.label}</span>
                        </button>
                    )
                })}
            </div>

            <label className="relative flex min-h-[56px] cursor-pointer items-center gap-3 rounded-[14px] border border-rm-gray-light bg-white px-3.5 py-2.5 transition-colors hover:border-rm-gray focus-within:border-rm-blue sm:hidden">
                <MotifIcon icon={selected.icon} selected={false} />
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="text-xs font-bold uppercase tracking-wide text-rm-gray-dark">Type de séjour</span>
                    <span className="truncate text-[15px] font-bold text-pale">{selected.label}</span>
                </span>
                <AppIcon iconClassName="fa-chevron-down" size="sm" className="text-[#4a5565]" />
                <select
                    value={value}
                    onChange={(event) => onChange(event.target.value as TravelMotif)}
                    aria-label="Type de séjour"
                    data-test="travel-motif-select"
                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                >
                    {TRAVEL_MOTIF_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </label>
        </div>
    )
}
